import React, { useState, useEffect, useCallback } from "react";
import { getConfirmDeleteDialog } from "../Components/confirmDeleteDialog";
import FormThemes from "../Themes/FormThemes";
import MainLayout from "../Themes/MainLayout";
import {
  InputAdornment,
  Grid,
  Typography,
  MenuItem,
  Button,
  Alert,
  Box,
  CssBaseline,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Popover,
  Switch,
  Paper,
  Pagination,
  Tooltip,
} from "@mui/material";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import {
  Add,
  Flag,
  FilterList,
  Search,
  } from "@mui/icons-material";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const columns = ["name", "code", "description", "created_by"];

const columnLabels = {
  name: "Name",
  code: "Code",
  description: "Description",
  created_by: "Created By",
  ...Object.fromEntries(
    columns
      .filter((c) => !["name", "code", "description", "created_by"].includes(c))
      .map((c) => [
        c,
        c
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" "),
      ])
  ),
};

function Permissions() {
  const { empId, boId } = useCurrentUser();
  const [data, setData] = useState([]);
  const formFields = ["name", "description"];
  const [visibleCols, setVisibleCols] = useState({
    name: true,
    description: true,
    created_by: true,
    ...Object.fromEntries(
      columns
        .filter((c) => !["name", "description", "created_by"].includes(c))
        .map((c) => [c, false])
    ),
  });
  const [filters, setFilters] = useState({ delete_flag: "ACTIVE" });
  const [statusOptions, setStatusOptions] = useState([]);
  const [filterAnchors, setFilterAnchors] = useState({});
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [alert, setAlert] = useState({ type: "", msg: "" });
  const [anchorSettings, setAnchorSettings] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [fieldErrors, setFieldErrors] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const query = new URLSearchParams({
        page,
        limit: rowsPerPage,
        ...filters,
        bo_id: boId,
      }).toString();

      const res = await fetch(`${BASE_URL}/api/permissions/paginated?${query}`);
      const json = await res.json();
      setData(json.data || []);
      setTotalPages(Math.ceil(json.total / json.limit));
    } catch (err) {
      console.error("Error fetching permissions:", err);
      setAlert({ type: "error", msg: "Failed to load permissions" });
    }
  }, [page, filters, boId, rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (alert.msg) {
      const timer = setTimeout(() => {
        setAlert({ type: "", msg: "" });
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [alert.msg]);

  useEffect(() => {
    const fetchStatusFlags = async () => {
      try {
        const res = await fetch(`${BASE_URL}/commonVariables/statusFlag`);
        const data = await res.json();
        setStatusOptions(data); // [{ code: "ACTIVE", label: "Active" }, ...]
      } catch (err) {
        console.error("Failed to fetch employee status flags:", err);
      }
    };
    fetchStatusFlags();
  }, []);

  const handleFilterOpen = (e, col) =>
    setFilterAnchors({ ...filterAnchors, [col]: e.currentTarget });
  const handleFilterClose = (col) =>
    setFilterAnchors({ ...filterAnchors, [col]: null });

  const handleAdd = () => {
    setFormData({});
    setFieldErrors({});
    setIsEditing(false);
    setOpenDialog(true);
  };

  const handleEdit = (permission) => {
    setFormData(permission);
    setFieldErrors({});
    setIsEditing(true);
    setOpenDialog(true);
  };

  const handleDelete = (permission) => {
    setSelectedPermission(permission);
    setDeleteId(permission.permission_id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId || !selectedPermission) return;
    try {
      const res = await fetch(
        `${BASE_URL}/api/permissions/${deleteId}/${selectedPermission.bo_id}`,
        {
          method: "DELETE",
        }
      );
      if (!res.ok) throw new Error("Delete failed");
      await fetchData();
      setAlert({ type: "success", msg: "Permission deleted successfully" });
    } catch (err) {
      console.error("Error deleting permission:", err);
      setAlert({ type: "error", msg: "Failed to delete permission" });
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
      setSelectedPermission(null);
    }
  };

  const handleToggleFlag = async (permission) => {
    const updatedFlag = permission.delete_flag === "Y" ? "N" : "Y";
    // Only send fields we want to update
    const payload = {
      delete_flag: updatedFlag,
      modified_by: empId,
      bo_id: boId,
      modified_at: new Date(),
      modified_on_date: new Date(),
    };
    try {
      const res = await fetch(
        `${BASE_URL}/api/permissions/${permission.permission_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error("Toggle failed");

      await fetchData();
      setAlert({ type: "success", msg: "Toggle flag updated Successfully.." });
    } catch (err) {
      console.error(err);
      setAlert({ type: "error", msg: "Delete failed" });
    }
  };

  const handleSave = async () => {
    // Frontend validation before API call
    const errors = {};
    if (!formData.name || !formData.name.trim()) errors.name = "Name is required";
    if (!formData.description || !formData.description.trim()) errors.description = "Description is required";
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    // Duplicate check against existing data
    const isDuplicate = data.some((item) => {
      if (isEditing && item.permission_id === formData.permission_id) return false;
      return (
        item.name?.trim().toLowerCase() === formData.name.trim().toLowerCase() &&
        item.description?.trim().toLowerCase() === formData.description.trim().toLowerCase()
      );
    });
    if (isDuplicate) {
      setAlert({ type: "error", msg: "A permission with the same name and description already exists" });
      return;
    }

    setFieldErrors({});
    try {
      const method = isEditing ? "PUT" : "POST";
      const url = isEditing
        ? `${BASE_URL}/api/permissions/${formData.permission_id}`
        : `${BASE_URL}/api/permissions/${boId}`;
      // Exclude audit columns when updating
      const { created_at, created_on_date, created_by, ...payloadData } =
        formData;

      const payload = isEditing
        ? {
            ...payloadData,
            modified_by: empId,
            bo_id: boId,
            modified_at: new Date(),
            modified_on_date: new Date(),
          }
        : { ...formData, empId, bo_id: boId };

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const msg = errData.message || errData.error || "";
        if (msg.toLowerCase().includes("duplicate") || msg.toLowerCase().includes("unique") || msg.toLowerCase().includes("already exists")) {
          throw new Error("A permission with the same name and description already exists");
        }
        throw new Error(msg || "Failed to save permission");
      }
      await fetchData();
      setAlert({
        type: "success",
        msg: isEditing
          ? "Record Updated successfully!"
          : "Record added successfully!",
      });
      setOpenDialog(false);
    } catch (err) {
      console.error(err);
      setAlert({ type: "error", msg: err.message || "Save failed" });
    }
  };

  return (
    <MainLayout>
    <ThemeProvider theme={Table_theme}>


        {alert.msg && (
          <Alert
            severity={alert.type}
            sx={{ mb: 2 }}
            onClose={() => setAlert({ type: "", msg: "" })}
          >
            {alert.msg}
          </Alert>
        )}

        <Box
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          mb={2}
          gap={1.5}
        >
          <IconButton onClick={(e) => setAnchorSettings(e.currentTarget)}>
            <SettingsIcon />
          </IconButton>

          <Popover
            open={Boolean(anchorSettings)}
            anchorEl={anchorSettings}
            onClose={() => setAnchorSettings(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 1,
                p: 1,
                width: 250,
                maxHeight: 400,
                overflowY: "auto",
                borderRadius: 2,
              },
            }}
          >
            {columns.map((col) => (
              <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    border: "1px solid #ccc",
                    borderRadius: 1.5,
                    px: 2,
                    py: 1,
                    backgroundColor: "#fff",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      color: "#333",
                      fontWeight: "bold",
                    }}
                  >
                    {columnLabels[col]}
                  </Typography>
                  <Switch
                    size="small"
                    checked={visibleCols[col]}
                    onChange={(e) =>
                      setVisibleCols((prev) => ({
                        ...prev,
                        [col]: e.target.checked,
                      }))
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#2DB0E4",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#2DB0E4",
                        },
                    }}
                  />
                </Box>
              </MenuItem>
            ))}
          </Popover>

          <Button
            variant="contained"
            size="small"
            onClick={handleAdd}
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              px: 2.5,
              height: 36,
              backgroundColor: "#2DB0E4",
            }}
            startIcon={<Add />}
          >
            Add
          </Button>

          <TextField
            size="small"
            placeholder="Search…"
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, search: e.target.value }));
              setPage(1);
            }}
            sx={{
                       width: 250,
                       "& input": { fontSize: 14 }, // input + placeholder
                       "& input::placeholder": { fontSize: 12 }, // placeholder only
                     }}
                     InputProps={{
                       sx: {
                         height: 36, // Reduce overall input height
                         paddingRight: 0,
                       },
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {/* Status Dropdown */}
          <TextField
            select
            size="small"
            label="Status"
            value={filters.delete_flag || "All"}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, delete_flag: e.target.value }))
            }
             sx={{
            width: 200,
            // Make height match the 36px of the search field
            "& .MuiInputBase-root": {
              height: 36,
            },

            // Fix vertical alignment of text
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              fontSize: 12,
              padding: "0 14px",
            },

            // Label font size
            "& .MuiInputLabel-root": { fontSize: "14px" },
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  "& .MuiMenuItem-root": {
                    fontSize: "14px",
                    minHeight: "32px",
                  },
                },
              },
            },
          }}
            InputProps={{ sx: { height: 35, alignItems: "center", },
            }}
          >
            <MenuItem value="All">All</MenuItem>
            {statusOptions.map((opt) => (
              <MenuItem key={opt.code} value={opt.code.toUpperCase()}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ width: "100%", overflowX: "auto", px: 0, py: 2, mt: "-10px" }}>
          <ThemeProvider theme={Table_theme}>
            <CssBaseline />
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        whiteSpace: "nowrap",
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      Actions
                    </TableCell>
                    {columns.map(
                      (col) =>
                        visibleCols[col] && (
                          <TableCell
                            key={col}
                            sx={{
                              whiteSpace: "nowrap",
                              textAlign: "left",
                              verticalAlign: "middle",
                            }}
                          >
                            <Box
                              display="inline-flex"
                              alignItems="center"
                              justifyContent="center"
                              sx={{ gap: 0.5 }}
                            >
                              <strong>{columnLabels[col]}</strong>
                              {![
                                "permission_count",
                                "user_count",
                                "group_count",
                              ].includes(col) && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleFilterOpen(e, col)}
                                >
                                  <FilterList sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}
                            </Box>
                            <Popover
                              open={!!filterAnchors[col]}
                              anchorEl={filterAnchors[col]}
                              onClose={() => handleFilterClose(col)}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                            >
                              <Box
                                p={2}
                                display="flex"
                                flexDirection="column"
                                gap={1}
                                width={200}
                              >
                                <TextField
                                  size="small"
                                  placeholder={`Filter ${columnLabels[col]}`}
                                  value={filters[col] || ""}
                                  onChange={(e) =>
                                    setFilters({
                                      ...filters,
                                      [col]: e.target.value,
                                    })
                                  }
                                />
                              <Box
                                  mt={1}
                                  display="flex"
                                  justifyContent="flex-end"
                                  gap={1}
                                >
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleFilterClose(col)}
                                    // sx={{ minWidth: 70 }}
                                  >
                                    Apply
                                  </Button>
                                  <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => {
                                      const newFilters = { ...filters };
                                      delete newFilters[col];
                                      setFilters(newFilters);
                                      handleFilterClose(col);
                                    }}
                                    // sx={{ minWidth: 70 }}
                                  >
                                    Clear
                                  </Button>
                                </Box>
                              </Box>
                            </Popover>
                          </TableCell>
                        )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((permission) => (
                    <TableRow key={permission.permission_id}>
                      <TableCell
                        sx={{ whiteSpace: "nowrap", textAlign: "center" }}
                      >
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip  title="Edit" >
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(permission)}
                            sx={{ color: "#2DB0E4" }}
                          >
                            <EditOutlinedIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                          </Tooltip>
                          <Tooltip  title="Delete" >
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(permission)}
                          >
                            <DeleteIcon
                              sx={{ color: "#FF0C0C", width: 17, height: 17 }}
                            />
                          </IconButton>
                          </Tooltip>
                           <Tooltip  title="Toggle Delete Flag" >
                          <IconButton
                            onClick={() => handleToggleFlag(permission)}
                            >
                            <Flag
                              sx={{
                                color:
                                  permission.delete_flag === "N"
                                    ? "#2E9F50"
                                    : "#FF0C0C",
                                width: 17,
                                height: 17,
                              }}
                            />
                          </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      {columns.map(
                        (col) =>
                          visibleCols[col] && (
                            <TableCell key={col}>
                              {permission[col] !== null &&
                              permission[col] !== undefined
                                ? permission[col]
                                : "-"}
                            </TableCell>
                          )
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, v) => setPage(v)}
                color="primary"
                variant="outlined"
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: "8px",
                    minWidth: "32px",
                    height: "32px",
                    fontSize: "14px",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#b3dbea !important",
                    color: "black",
                    fontWeight: "bold",
                  },
                  "& .MuiPaginationItem-ellipsis": {
                    display: "none",
                  },
                }}
              />
            </Box>
          </ThemeProvider>
        </Box>

        {/* Permission Form Dialog */}
        <ThemeProvider theme={FormThemes}>
          <CssBaseline />
          {getConfirmDeleteDialog({
            open: confirmOpen,
            onClose: () => {
              setConfirmOpen(false);
            },
            onConfirm: confirmDelete,
            title: "Confirm Deletion",
            contentText: selectedPermission?.name
              ? `Are you sure you want to permanently delete the permission ${selectedPermission.name}?`
              : "Are you sure you want to permanently delete this permission?",
          })}
          <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            fullWidth
            maxWidth="sm"
            sx={{
              "& .MuiDialog-paper": {
                borderRadius: 5,
                overflow: "visible",
                width: "500px",
                maxWidth: "95vw",
              },
            }}
          >
            <DialogTitle
              sx={{
                borderRadius: "12px",
                bgcolor: "#f5f5f5",
                p: 1,
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              {isEditing ? "Edit Permission" : "Add Permission"}
              <IconButton
                onClick={() => setOpenDialog(false)}
                sx={{ position: "absolute", right: 8, top: 5 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent
              dividers
              sx={{
                p: "18px 8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                backgroundColor: "#fff",
               // mt: 1,
              }}
            >
              <Grid container spacing={2} sx={{ width: "100%", px: 0, m: 0 }}>
                {formFields.map((col, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <TextField
                      label={columnLabels[col]}
                      name={col}
                      required
                      type="text"
                      value={formData[col] || ""}
                      onChange={(e) => {
                        setFormData({ ...formData, [col]: e.target.value });
                        if (fieldErrors[col]) setFieldErrors((prev) => ({ ...prev, [col]: "" }));
                      }}
                      error={!!fieldErrors[col]}
                      helperText={fieldErrors[col] || ""}
                      InputLabelProps={{ shrink: true }}
                       sx={{
                        width: 200,
                        /* LABEL POSITION */
                        "& .MuiInputLabel-outlined": {
                          transform: "translate(12px, -9px) scale(0.75)",
                          whiteSpace: "nowrap",
                        },
                        /* NOTCH (legend) */
                        "& .MuiOutlinedInput-notchedOutline legend": {
                          padding: 0,
                          maxWidth: "none",
                        },
                        /*  REMOVE EXTRA RIGHT SPACE (KEY FIX) */
                        "& .MuiOutlinedInput-notchedOutline legend span": {
                          paddingRight: "2px", // minimal single space
                          paddingLeft: "0px",
                        },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: "8px 24px", justifyContent: "flex-end" }}>
              <Button
                onClick={() => setOpenDialog(false)}
                variant="outlined"
                color="inherit"
                sx={{ mr: 1, color: "#555", borderColor: "#ccc" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                color="primary"
                sx={{
                  fontWeight: "bold",
                  borderRadius: "8px",
                  px: 2.5,
                  height: 36,
                  ...(isEditing
                    ? {
                        backgroundColor: "#4CAF50",
                        "&:hover": { backgroundColor: "#45a049" },
                      }
                    : {
                        backgroundColor: "#2DB0E4",
                        "&:hover": { backgroundColor: "#1D87B0" },
                      }),
                }}
              >
                {isEditing ? "Update" : "Save"}
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
     </ThemeProvider>
     </MainLayout>
  );
}

export default Permissions;
